// Third-party
import get from 'lodash/get';
import randomColor from 'randomcolor';
import moment from 'moment';

// DAOs
import { DataPoints } from '../../modules/proposal/DAOs/DataPoints';

// Utils
import { HOLDING_TYPES } from '../portfolios';
import { formatMessage } from '../i18n';
import { generateTempId } from '../random';
import { deepClone } from '../objects';
import { trimRollingData, calculateRiskMetrics, getCommonTimestamps, getValidPeriods, calculateCorrelationMatrix } from '../calculation-utils';
import { periodToMonths } from '../time';
import { getInitialUserPreferences } from '@/utils/users';
import { getMaxEndDate } from '../dates';

// Enums
import { PORTFOLIO_ASSET_ALLOCATION } from '../../enums/AssetAllocation';
import { ITEM_COLORS } from '../colors';
import { benchmarkIsFull } from './benchmarks';
import {
  TIME_PERIODS,
  DISPLAYED_CREDIT_QUALITY_CATEGORIES,
  CREDIT_QUALITY_CATEGORIES,
  COMPARISON_ITEM_TYPES,
  CREDIT_QUALITY_BELOW_B_CATEGORIES,
  DEFAULT_CORRELATION_VALUES,
  Interval,
  MAX_EXPANDED_ITEMS_IN_PORTFOLIO,
  ComparisonFlagsType,
} from '../constants';
import { DISCLAIMER_KEYS } from '../disclaimers/constants';
import { COMPARISON_MODIFIED_ITEM_KEYS } from '@/enums/Comparison';

// Types
import { ComparisonState, ComparisonItemsFilter } from '../../redux/modules/types/comparison';
import { applyPermissionsToAvailableActions, ENTITY_CLASS } from '../availableActions';
import { createComparisonContact } from '../contacts';
import { itemIsMissingWeeklyData } from '../disclaimers/charts';
import { getComparisonItemHoldings } from './items';
import holdingsToComparisonItems from './instrument-to-comparison-item/holdings-to-comparison-items';
import { USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_DETAIL, USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_HORIZON } from '@/redux/modules/user-preferences';
import Dictionary from '@/types/dictionary';
import ComparisonItem from '@/types/comparison-item';
import Holdings from '@/types/holdings';
import FundAllocation from '@/types/fund-allocation';
import LocalizedString from '@/types/localized-string';
import QuickSearchItem from '@/types/quick-search-item';
import Nullable from '@/types/nullable';
import User from '@/types/user';
import Contact from '@/types/contact';
import Comparison from '@/types/comparison';
import ReducedComparison from '@/types/reduced-comparison';
import SharingInfo from '@/types/sharing-info';
import SelectedCategories from '@/types/selected-categories';
import ComparisonCategory from '@/types/comparison-category';
import ComparisonField from '@/types/comparison-field';
import PortfolioHolding from '@/types/portfolio-holding';
import Holding from '@/types/holding';
import AppRegion from '@/types/app-region';
import Benchmark from '@/types/benchmark';
import BenchmarkRisk from '@/types/benchmark-risk';
import PortfolioRisk from '@/types/portfolio-risk';
import ComparisonItemPerformance from '@/types/comparison-item-performance';
import CorrelationMetric from '@/types/correlation-metric';
import CorrelationDataInput from '@/types/correlation-data-input';
import ComparisonHolding from '@/types/comparison-holding';
import ComparisonOptions from '@/types/comparison-options';
import GrowthChartOptions from '@/types/growth-chart-options';
import SortDirection from '@/types/sort-direction';
import PERIOD_END_DATA_OPTIONS from '@/types/period-end-data-options';
import CommonInception from '@/types/common-inception';
import RollingPerformance from '@/types/rolling-performance';
import { PERMISSIONS, userHasPermission, userHasPermissionEnabled } from '../permissions';
import { hasComparisonFlag } from '@/redux/modules/comparison/utils/hasComparisonFlag';

export { default as benchmarkToComparisonItem } from './instrument-to-comparison-item/benchmark-to-comparison-item';
export { default as fundToComparisonItem } from './instrument-to-comparison-item/fund-to-comparison-item';
export { default as portfolioToComparisonItem } from './instrument-to-comparison-item/portfolio-to-comparison-item';
export { default as stockToComparisonItem } from './instrument-to-comparison-item/stock-to-comparison-item';
export { default as holdingsToComparisonItems } from './instrument-to-comparison-item/holdings-to-comparison-items';

export const RISK_ALLOCATION_KEYS = {
  CASH: 'CASH',
  EQUITY: 'EQUITY',
  FIXED_INCOME: 'FIXED_INCOME',
  OTHER: 'OTHER',
};

export enum COMPARISON_TYPE {
  COMPLIANCE = 'COMPLIANCE',
  CONTACT = 'CONTACT',
  DRAFT = 'DRAFT',
}

/**
 * Items types that can be added inside a products group.
 */
export const VALID_GROUP_PRODUCTS = [COMPARISON_ITEM_TYPES.FUND, COMPARISON_ITEM_TYPES.STOCK, COMPARISON_ITEM_TYPES.PORTFOLIO, COMPARISON_ITEM_TYPES.ALT];

/**
 * Get the earliest common data points for an array of comparison items (used for graphing)
 * @param items the comparison items
 * @param {Date} startDate optional start date to overwrite the starting point
 */
export const getCommonDataPoints = (
  items: ComparisonItem[],
  interval: Interval,
  startDate?: Date,
  direction: SortDirection = 'ASC',
  userLongHistory?: boolean,
) => {
  const rollings = items.reduce((acc: RollingPerformance[], item) => {
    // for normal (short) history, we return all holdings
    let holdings: RollingPerformance[] = getComparisonItemHoldings(item, true).map((h: ComparisonHolding) =>
      h.rolling ? h.rolling : { monthly: [], weekly: [] },
    );
    if (userLongHistory && (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP)) {
      // for long history, we only return the longuest one
      let longuestHolding = holdings[0];
      for (const holding of holdings) {
        if (holding[interval].length > longuestHolding[interval].length) {
          longuestHolding = holding;
        }
      }
      if (longuestHolding != null) {
        holdings = [longuestHolding];
      }
    }
    return acc.concat(holdings);
  }, [] as RollingPerformance[]);

  return getCommonTimestamps(rollings, direction, startDate);
};

export const getAssetDetailsForRiskComparison = (item?: ComparisonItem, splitCashAndFixedIncome?: boolean): Dictionary<number> => {
  if (!item || !item.allocations || !item.allocations.asset) {
    return {};
  }

  const assets = item.allocations.asset as Dictionary<number>;

  const defaultAllocations = {
    [RISK_ALLOCATION_KEYS.EQUITY]: 0,
    [RISK_ALLOCATION_KEYS.FIXED_INCOME]: 0,
    [RISK_ALLOCATION_KEYS.OTHER]: 0,
  };

  const result = Object.keys(assets).reduce((acc: Dictionary<number>, key: string) => {
    const value = assets[key];
    let allocKey;

    switch (key) {
      case PORTFOLIO_ASSET_ALLOCATION.CANADIAN_EQ:
      case PORTFOLIO_ASSET_ALLOCATION.US_EQ:
      case PORTFOLIO_ASSET_ALLOCATION.FOREIGN_EQ: {
        // Stop here - equity is already pre-calculated in a global field (see case `PORTFOLIO_ASSET_ALLOCATION.EQ`)
        return acc;
      }
      case PORTFOLIO_ASSET_ALLOCATION.EQ: {
        allocKey = RISK_ALLOCATION_KEYS.EQUITY;
        break;
      }
      case PORTFOLIO_ASSET_ALLOCATION.FIXED_INCOME:
        allocKey = RISK_ALLOCATION_KEYS.FIXED_INCOME;
        break;
      case PORTFOLIO_ASSET_ALLOCATION.CASH:
        allocKey = splitCashAndFixedIncome ? RISK_ALLOCATION_KEYS.CASH : RISK_ALLOCATION_KEYS.FIXED_INCOME;
        break;
      default:
        allocKey = RISK_ALLOCATION_KEYS.OTHER;
        break;
    }

    acc[allocKey] = (acc[allocKey] || 0) + value;
    return acc;
  }, defaultAllocations);

  const mixedAssetsWeight = item ? DataPoints.getMixedAssetAllocationPortion(item) : 0;
  result[RISK_ALLOCATION_KEYS.OTHER] += mixedAssetsWeight;

  return result;
};

/**
 * Generates a unique color for a comparison item.
 *
 * @param {ComparisonItem[]} items Existing comparison items.
 * @return {string} Unique color for the new comparison item.
 */
export const generateNewItemColor = (items: { color?: string }[]): string => {
  const presetColor = ITEM_COLORS.find(color => !items.some(item => item.color === color));
  return presetColor || randomColor({ luminosity: 'dark' });
};

/**
 * Split comparison holdings into stocks and funds
 *
 * @param {ComparisonItem} item Comparison item.
 * @return {[Fund[], Stock[]]} Arrays of funds and stocks
 */
export const splitComparisonItemHoldings = (item: ComparisonItem) => {
  const funds = item.holdings.filter(h => h.type === HOLDING_TYPES.FUND);
  const stocks = item.holdings.filter(h => h.type === HOLDING_TYPES.STOCK);
  return { funds, stocks };
};

/**
 * Convert comparison item to rich message content
 *
 * @param {ComparisonItem} item Comparison item.
 * @return {QuickSearchItem} rich message content.
 */
export const comparisonItemToRichMessage = (item: ComparisonItem): QuickSearchItem => {
  const result: QuickSearchItem = {
    _id: item._id,
    name: item.name,
    type: item.type,
    color: item.color,
  };
  if (result.type === COMPARISON_ITEM_TYPES.FUND) {
    result.code = item.holdings[0].symbol;
  }
  if (result.type === COMPARISON_ITEM_TYPES.STOCK) {
    result.code = item.holdings[0].symbol;
  }
  if (result.type === COMPARISON_ITEM_TYPES.PORTFOLIO) {
    result.holdings = item.holdings.map(h => ({ symbol: h.symbol })) as { symbol: string }[];
  }
  return result;
};

/**
 * Maps the comparison id to the comparison items
 *
 * @param {ComparisonItem[]} items Comparison items.
 * @return {ComparisonItem[]} the items with the comparisonId
 */
export const mapComparisonId = (items: ComparisonItem[], comparisonId: string): ComparisonItem[] =>
  items.map(item => (!item.sharedFrom ? { ...item, sharedFrom: comparisonId } : { ...item }));

/**
 * Updates the endDate for each graph to be today's date and adjust the startDate
 * to be within the current interval
 *
 * @param {ComparisonOptions} options Comparison graph options.
 * @returns {ComparisonOptions} Comparison graph options with dates updated.
 */
export const updateChartDatesToToday = (options: ComparisonOptions, monthEndDataOption?: PERIOD_END_DATA_OPTIONS) => {
  const endDate = getMaxEndDate(monthEndDataOption);

  const updatedChartOptions = Object.keys(options).reduce((acc, chart: string) => {
    const chartOption = options[chart as keyof ComparisonOptions];
    if (chartOption) {
      // Cast chartOption to type that has a startDate property
      const currentStartDate = (chartOption as GrowthChartOptions).startDate;
      // Cast chartOption to type that has an endDate property
      const currentEndDate = (chartOption as GrowthChartOptions).endDate;
      const monthsRange = currentStartDate && currentEndDate ? moment(currentEndDate).diff(currentStartDate, 'months') : null;

      const newEndDate = currentEndDate ? endDate : currentEndDate;
      const newStartDate = monthsRange ? moment(endDate).startOf('month').subtract(monthsRange, 'months').toDate() : currentStartDate;

      return {
        ...acc,
        [chart]: {
          ...chartOption,
          startDate: newStartDate,
          endDate: newEndDate,
        },
      };
    }

    return acc;
  }, {} as ComparisonOptions);

  return updatedChartOptions;
};

/**
 * Generates a localized new name for an item being copied.
 * Ex: 'Copy of ...', 'Copy 2 of ...', etc.
 *
 * @param {string} itemName Name of the item being copied.
 * @returns {string} Name of the copied item.
 */
export const generateItemCopyName = (itemName: string, i18nKey: string) => {
  const regex = new RegExp(formatMessage(i18nKey, { number: '(\\d ){0,1}' }));
  const existingPrefix = itemName.match(regex);

  if (existingPrefix) {
    const copyNumber = existingPrefix[0].match(/\d/) || 1;
    const prefix = formatMessage(i18nKey, { number: copyNumber ? `${Number(copyNumber) + 1} ` : '' });
    return prefix + itemName.replace(existingPrefix[0], '');
  }

  return formatMessage(i18nKey, { number: '' }) + itemName;
};

export const generateDraftComparison = (user: Nullable<User> | undefined, contact: Contact | undefined = undefined) => {
  const { hypotheticalReturnChartHorizon, performanceAndRiskData, hypotheticalReturnChartDetail, baseCurrency } =
    user?.preferences?.comparisonSettings || getInitialUserPreferences(user);
  let months: number | null = 36;
  let defaultStartDate: Nullable<Date> = moment().startOf('month').subtract(months, 'month').toDate();

  switch (hypotheticalReturnChartHorizon) {
    case USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_HORIZON.YTD:
      months = periodToMonths(TIME_PERIODS.YT);
      defaultStartDate = moment().startOf('month').subtract(months, 'month').toDate();
      break;
    case TIME_PERIODS['1Y']:
    case TIME_PERIODS['3Y']:
    case TIME_PERIODS['5Y']:
    case TIME_PERIODS['10Y']:
      months = periodToMonths(hypotheticalReturnChartHorizon);
      defaultStartDate = moment().startOf('month').subtract(months, 'month').toDate();
      break;
    case TIME_PERIODS.INC:
    case TIME_PERIODS.CI:
      defaultStartDate = null;
      break;
  }

  const defaultChartOptions = {
    startDate: defaultStartDate,
    endDate: moment().toDate(),
  };

  const tempId = generateTempId();
  let comparison = {
    _id: tempId,
    correlationId: tempId.replace('new-', ''),
    comparisonType: COMPARISON_TYPE.DRAFT,
    items: [],
    name: formatMessage('Comparison.DefaultName'),
    isDirty: false,
    riskPeriod: TIME_PERIODS['3Y'],
    fundRiskPeriod: TIME_PERIODS['3Y'],
    portfolioRiskPeriod: TIME_PERIODS['3Y'],
    groupRiskPeriod: TIME_PERIODS['3Y'],
    options: {
      growth: {
        ...defaultChartOptions,
      },
      drawdown: defaultChartOptions,
      fundGrowth: {
        ...defaultChartOptions,
      },
      portfolioGrowth: {
        ...defaultChartOptions,
      },
      groupGrowth: {
        ...defaultChartOptions,
      },
      riskReward: {},
    },
    calendarYearPeriod: TIME_PERIODS['5Y'],
    fundCalendarYearPeriod: TIME_PERIODS['5Y'],
    portfolioCalendarYearPeriod: TIME_PERIODS['5Y'],
    groupCalendarYearPeriod: TIME_PERIODS['5Y'],
    selectedBenchmarkId: null,
    toggledCategories: [],
    flags: [],
  } as Comparison;

  if (performanceAndRiskData) {
    if (performanceAndRiskData === PERIOD_END_DATA_OPTIONS.AUTO && user?.preferences?.autoPerformanceAndRiskData) {
      comparison.monthEndDataOption = user?.preferences?.autoPerformanceAndRiskData as PERIOD_END_DATA_OPTIONS;
      comparison.autoMonthEndData = true;
    } else {
      comparison.monthEndDataOption = performanceAndRiskData as PERIOD_END_DATA_OPTIONS;
    }
  }

  if (baseCurrency) {
    comparison.showBaseCurrencies = baseCurrency;
  }

  if (baseCurrency) {
    comparison.showBaseCurrencies = baseCurrency;
  }

  applyDefaultLongerHistorySettings(comparison, user);

  if (hypotheticalReturnChartDetail) {
    comparison.options = {
      ...comparison.options,
      growth: {
        ...comparison.options?.growth,
        useMonthly: setHypotheticalReturnChartDetail(hypotheticalReturnChartDetail),
      },
    };
  }

  if (contact != null) {
    comparison.contact = createComparisonContact(contact);
    comparison.comparisonType = COMPARISON_TYPE.CONTACT;
    if (userHasPermission(user, PERMISSIONS.CONTACTS_LOCALE) && contact.locale != null) {
      comparison.proposalLocale = contact.locale;
    }
  }

  if (user != null) {
    comparison = applyPermissionsToAvailableActions(comparison, ENTITY_CLASS.COMPARISONS, user);
  }

  return comparison;
};

/**
 * Sets shortHistory to true on the comparison if the user has both
 * SHORT_HISTORY and DEFAULT_LONGER_HISTORY permissions.
 *
 * @param {Comparison} comparison The comparison object to potentially modify.
 * @param {Nullable<User> | undefined} user The user to check permissions for.
 */
export const applyDefaultLongerHistorySettings = (comparison: Comparison, user: Nullable<User> | undefined): void => {
  if (userHasPermissionEnabled(user, PERMISSIONS.SHORT_HISTORY) && userHasPermissionEnabled(user, PERMISSIONS.DEFAULT_LONGER_HISTORY)) {
    comparison.shortHistory = true;
  }
};

/**
 * Determine if the item is a benchmark or not
 * @param {ComparisonItem} item the item to test
 */
export const isBenchmark = (item: { type: string }) => item.type === COMPARISON_ITEM_TYPES.BENCHMARK;

/**
 * Determine if the comparison item is missing benchmark data
 * @param {ComparisonItem} item the item to test
 * @returns {boolean}
 */
export const isMissingBenchmark = (item: ComparisonItem) => {
  if (item.disclaimers) {
    return DISCLAIMER_KEYS.PORTFOLIO_MISSING_BENCHMARK in item.disclaimers;
  }
  return false;
};

export const comparisonCreatedInPast = (comparison: ReducedComparison) => comparison.lastRunAt && !moment(comparison.lastRunAt).isSame(moment(), 'day');

export const comparisonIsCompliance = (comparison: ReducedComparison) => comparison.comparisonType === COMPARISON_TYPE.COMPLIANCE;

export const comparisonIsEditable = (comparison: ReducedComparison) => {
  const { availableActions } = comparison;
  return availableActions != null ? availableActions.edit : true;
};

export const comparisonIsReadOnly = (comparison: ReducedComparison) => {
  const canEdit = comparisonIsEditable(comparison);
  const isCompliance = comparisonIsCompliance(comparison);
  const isPastContactComparison = comparison.contact && comparisonCreatedInPast(comparison);
  const isSharedWithClient = hasComparisonFlag(comparison, ComparisonFlagsType.SHARED_WITH_CLIENT);

  return !canEdit || isPastContactComparison || isCompliance || isSharedWithClient;
};

export const comparisonHasGifItems = (comparison: Comparison) => comparison.gifItems && comparison.gifItems.length > 0;

export const comparisonHasItems = (comparison: Comparison, filterBenchmarks?: boolean) => {
  if (comparisonHasGifItems(comparison)) {
    return true;
  }

  if (filterBenchmarks) {
    return comparison.items.filter(item => !isBenchmark(item)).length > 0;
  }

  return comparison.items?.length > 0;
};

/**
 *
 * @param {Comparison} comparison source comparison to generate a shared copy from
 * @param {SharingInfo} sharingInfo sharing information from the user
 * @param {ComparisonItem[]} items items to override (used for proposal sharing)
 * @returns {Comparison} the shared comparison with contacts and rollings removed
 */
export const generateSharedComparison = (comparison: Comparison, sharingInfo: Nullable<SharingInfo>) => {
  const sharedComparison = deepClone(comparison) as Comparison;

  if (comparison.selectedCategories) {
    sharedComparison.selectedCategories = deepClone(comparison.selectedCategories) as SelectedCategories;
  }

  delete sharedComparison._id;
  delete sharedComparison.contact;
  delete sharedComparison.isDirty;

  for (const item of sharedComparison.items) {
    delete item.sharedFrom;
    delete item.sharedWith;
    delete item.performance.rolling;

    for (const holding of item.holdings) {
      delete holding.rolling;
    }

    if (sharingInfo) {
      item.providedBy = sharingInfo;
    }
  }

  return sharedComparison;
};

export const getCurrentStateComparison = (state: ComparisonState) => state.activeComparison;

export const getMonthEndDataOption = (state: ComparisonState): PERIOD_END_DATA_OPTIONS | undefined => {
  const comparison = getCurrentStateComparison(state);
  if (comparison === undefined) {
    if (state.loading.isLoading) {
      return state.loading.monthEndDataOption;
    }
    return undefined;
  }

  return comparison.monthEndDataOption;
};

/**
 * Returns only items that are fully allocated
 * @param items an array of comparison items
 */
export const getFilteredItems = (items: ComparisonItem[]) => items.filter(item => (isBenchmark(item) ? benchmarkIsFull(item.holdings) : item));

/**
 * Find all fields that are duplicated in the comparison table
 * Examples: 1Y Performance, 3Y Performance, Beta
 * @param {ComparisonCategory[]} categories categories to check
 * @returns {string[]} array of field keys that are duplicated
 */
export const getDuplicateFields = (categories: ComparisonCategory[]) => {
  const fields = categories.map((category: ComparisonCategory) => category.fields.map(f => f.key));

  const arr = [] as string[];
  const flattenedFields = arr.concat([], ...fields);

  return Object.values(flattenedFields).filter((e, i, a) => a.indexOf(e) !== i);
};

/**
 * Check if a field is a duplicate in the comparison table
 * Examples: 1Y Performance, 3Y Performance, Beta
 * @param {ComparisonField} field field to check
 * @param {string[]} duplicateFields array of field keys that are duplicated
 * @returns {boolean} 'True' if the field is a duplicate. 'False' otherwise.
 */
export const isDuplicateField = (field: ComparisonField, duplicateFields?: string[]) => duplicateFields && duplicateFields.includes(field.key);

/**
 * Convert portfolio holdings into comparison items.
 * @param holdings Portfolio holdings.
 * @param data Dictionary of holdings data.
 * @return Resulting comparison items.
 */

export const portfolioHoldingsToComparisonItems = (holdings: PortfolioHolding[], data: Dictionary<Holding>, appRegion: AppRegion) => {
  const reducedHoldings = holdings.reduce(
    (acc, h) => {
      if (!h.original.id) {
        return acc;
      }

      acc.push({
        id: h.original.id,
        symbol: h.symbol || undefined,
        type: h.original.type,
      });

      return acc;
    },
    [] as { id: string; symbol?: string; type: string }[],
  );

  return holdingsToComparisonItems(reducedHoldings, data, appRegion);
};

/**
 * Gets the item dates for specified path.
 * @param items Comparison items.
 * @param includeBenchmarks Include the dates for the benchmarks.
 */
export const getItemDates = (item: ComparisonItem, datesPath: string, portfolioDatesPath?: string, includeBenchmarks?: boolean) => {
  let dates: Date[] = [];

  if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && portfolioDatesPath) {
    dates = dates.concat(get(item.dates, portfolioDatesPath, []));
  }

  const date = get(item.dates, datesPath);
  if (date) {
    dates.push(date);
  }

  if (includeBenchmarks) {
    for (const type of ['market', 'riskFree']) {
      const benchmarks = get(item.benchmarks, type) as Benchmark[];

      if (benchmarks) {
        dates = dates.concat(
          Object.values(benchmarks)
            .map(b => get(b.dates, datesPath))
            .filter(d => d) as Date[],
        );
      }
    }
  }

  return dates;
};

/**
 * Gets the combined dates for all items for disclaimer purposes (risk, performance, allocations, holdings).
 * @param items Comparison items.
 * @param includeBenchmarks Include the dates for the benchmarks.
 */
export const getItemsCombinedDates = (items: ComparisonItem[], includeBenchmarks?: boolean) => {
  const reduceDates = (datesPath: string, portfolioDatesPath?: string) =>
    items.reduce((acc: Date[], item: ComparisonItem) => acc.concat(getItemDates(item, datesPath, portfolioDatesPath, includeBenchmarks)), []);

  return {
    risk: reduceDates('risk.endDate'),
    performance: reduceDates('growth', 'portfolioGrowths'),
    allocations: reduceDates('allocations', 'portfolioAllocations'),
    holdings: reduceDates('holdings', 'portfolioHoldings'),
  };
};

/**
 * Gets the latest dates for disclaimer purposes (risk, performance, allocations, holdings).
 * Meant to be used in a "As at {date}, unless otherwise indicated" format.
 *
 * @param items Comparison items.
 * @param includeBenchmarks Include the dates for the benchmarks.
 */
export const getLatestDates = (items: ComparisonItem[], includeBenchmarks?: boolean) => {
  const dates = getItemsCombinedDates(items, includeBenchmarks);
  return {
    risk: dates.risk.sort().reverse()[0],
    performance: dates.performance.sort().reverse()[0],
    allocations: dates.allocations.sort().reverse()[0],
    holdings: dates.holdings.sort().reverse()[0],
  };
};
/**
 * Swaps the risk metrics in the performance object with the provided risk (useful for toggling back to originalRisk)
 * @param {ComparisonItem} item the comparison item to override risk risk metrics for
 * @param {BenchmarkRisk | Partial<PortfolioRisk>} risk the risk object to add to the performance
 * @returns {ComparisonItemPerformance} the item's performance object
 */
export const overrideRiskMetrics = (item: ComparisonItem, risk: BenchmarkRisk | Partial<PortfolioRisk>): ComparisonItemPerformance => {
  const itemPerformance = { ...item.performance, ...risk };
  return itemPerformance;
};

export const setBenchmarkRisk = (
  items: ComparisonItem[],
  benchmark: ComparisonItem,
  commonInception: CommonInception | undefined = undefined,
  shortHistory: boolean = false,
) => {
  if (benchmarkIsFull(benchmark.holdings)) {
    for (const item of items) {
      if (!isBenchmark(item) && item.performance.rolling && benchmark.performance.rolling) {
        let itemRolling = deepClone(item.performance.rolling.monthly);
        if ((item.type === COMPARISON_ITEM_TYPES.PORTFOLIO || item.type === COMPARISON_ITEM_TYPES.GROUP) && !shortHistory) {
          const latestCommonTimestamp = getCommonDataPoints([item], Interval.Monthly, undefined, 'ASC', shortHistory);
          const earliestCommonTimestamp = getCommonDataPoints([item], Interval.Monthly, undefined, 'DESC', shortHistory);
          itemRolling = trimRollingData(itemRolling, latestCommonTimestamp.monthly, earliestCommonTimestamp.monthly);
        }
        const risk = calculateRiskMetrics(
          itemRolling,
          benchmark.performance.rolling.monthly,
          benchmark.performance.riskFreeRolling && benchmark.performance.riskFreeRolling.monthly,
          item.type === COMPARISON_ITEM_TYPES.STOCK || item.type === COMPARISON_ITEM_TYPES.BENCHMARK,
          commonInception,
        );
        item.performance = overrideRiskMetrics(item, risk);
        let endDate = null;
        if (
          item.performance.rolling &&
          item.performance.rolling.monthly.length > 0 &&
          benchmark.performance.rolling &&
          benchmark.performance.rolling.monthly.length > 0 &&
          benchmark.performance.riskFreeRolling &&
          benchmark.performance.riskFreeRolling.monthly.length > 0
        ) {
          const rollings = [item.performance.rolling, benchmark.performance.rolling, benchmark.performance.riskFreeRolling];
          const lastTimeStamp = getCommonTimestamps(rollings, 'DESC');
          endDate = new Date(lastTimeStamp.monthly);
        }
        item.dates = { ...item.dates, risk: { endDate } };
      }
    }
  }
};

/**
 * Generate the displayed name of a comparison's contact.
 * @param comparison Comparison
 */
export const getComparisonContactName = (comparison: Comparison | ReducedComparison) => {
  const { contact } = comparison;

  if (contact == null) {
    return '';
  }

  if (contact.fname != null && contact.lname != null) {
    return `${contact.fname} ${contact.lname}`;
  }

  return contact.fullName || contact.email || '';
};

/**
 * Return a tooltip string explaining comparison is readonly incl. date last run at.
 * @param comparison Comparison
 */
export const getReadOnlyTooltipMessage = (comparison?: Comparison) => {
  const comparisonDate = comparison && comparison.lastRunAt ? moment(comparison.lastRunAt) : null;

  if (!comparisonDate) {
    return '';
  }

  return formatMessage('Comparison.ReadOnlyTooltip', { date: comparisonDate.format(formatMessage('Common.DateFormats.Short')) });
};

/**
 * Generate the displayed credit quality categories.
 * @param item ComparisonItem
 */
export const splitCreditQualityForDisplay = (item: ComparisonItem) => {
  const creditQuality = item.allocations && item.allocations.creditQuality;

  return DISPLAYED_CREDIT_QUALITY_CATEGORIES.reduce(
    (credit, cat) => {
      if (!creditQuality) {
        return {
          ...credit,
          [cat]: null,
        };
      }

      if (cat === CREDIT_QUALITY_CATEGORIES.BELOW_B) {
        const categories = creditQuality.filter(c => CREDIT_QUALITY_BELOW_B_CATEGORIES.includes(c.name));

        return {
          ...credit,
          [cat]: categories.length > 0 ? categories.reduce((sum, val) => sum + val.weight, 0) : null,
        };
      }

      const value = creditQuality.find(c => c.name === cat);

      return {
        ...credit,
        [cat]: value ? value.weight : null,
      };
    },
    {} as Dictionary<number | null>,
  ) as Dictionary<number | null>;
};

export const comparisonCategoryIsShown = (comparison: Comparison, category: ComparisonCategory) =>
  !category.toggleable || comparison.toggledCategories.includes(category.key);

export const comparisonHasFundDisclaimers = (items: ComparisonItem[]) =>
  items.some(item => !!item.disclaimers && !!item.disclaimers[DISCLAIMER_KEYS.INSTRUMENT_CUSTOM_DISCLAIMER]);

/**
 * Sums up the total equity portion of the item (Canadian, United States, foreign, etc.).
 *
 * @param item Comparison item
 */
export const getComparisonItemTotalEquity = (item: ComparisonItem) => {
  const allocations = item.allocations && item.allocations.asset;

  if (!allocations) {
    return undefined;
  }

  if (allocations[PORTFOLIO_ASSET_ALLOCATION.EQ] != null) {
    return allocations[PORTFOLIO_ASSET_ALLOCATION.EQ];
  }

  return Object.keys(allocations)
    .filter(key => key.toLowerCase().indexOf('equity') > -1)
    .reduce((sum, key) => sum + get(item, `allocations.asset["${key}"]`, 0), 0);
};

/**
 * Retrieve items from comparison that match the type specified. (ie: all portfolios)
 * @param comparison Comparison to get the items from.
 * @param type Type of items to retrieve from the comparison.
 */
export const getComparisonItemsByType = (comparison: Comparison, type: COMPARISON_ITEM_TYPES) => comparison.items.filter(item => item.type === type);

/**
 * Calculates the correlation value between each item in a comparison
 * @param comparison Comparison to get the items from.
 */
export const calculateComparisonCorrelations = (comparison: Comparison): CorrelationMetric => {
  const { items: comparisonItems } = comparison;
  const itemsCorrelation = { ...DEFAULT_CORRELATION_VALUES };
  // Timestamp expected is the end of the previous month
  const now = new Date();
  const endOfMonthTimestamp = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0)).getTime();
  // Return all periods as valid
  const validPeriods = getValidPeriods(0, endOfMonthTimestamp);
  const correlationItems: CorrelationDataInput[] = comparisonItems.map(item => {
    let rolling = item.performance.rolling != null ? item.performance.rolling.monthly : [];
    if (item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && !comparison.shortHistory) {
      const latestCommonTimestamp = getCommonDataPoints([item], Interval.Monthly, undefined, 'ASC', comparison.shortHistory);
      const earliestCommonTimestamp = getCommonDataPoints([item], Interval.Monthly, undefined, 'DESC', comparison.shortHistory);
      rolling = trimRollingData(rolling, latestCommonTimestamp.monthly, earliestCommonTimestamp.monthly);
    }

    return {
      _id: item._id,
      rolling,
    };
  });

  for (const validPeriod of validPeriods) {
    const { period, sliceCount } = validPeriod;
    itemsCorrelation[period] = calculateCorrelationMatrix(correlationItems, sliceCount);
  }

  return itemsCorrelation;
};

export const comparisonShouldForceMonthlyData = (comparison: Comparison): boolean => {
  const optionSets = [comparison.options?.growth, comparison.options?.drawdown];

  return comparison.items.some(item =>
    optionSets.some(options => {
      if (options && options.startDate && options.endDate) {
        return itemIsMissingWeeklyData(item, options.startDate, options.endDate);
      }

      return false;
    }),
  );
};

/**
 * Refresh the holdings for all group-type comparison items.
 * Put simply, we combine the holdings of all items from the
 * group, and then reallocate their weight.
 *
 * @param comparison Comparison to refresh
 */
export const refreshComparisonGroupItemsHoldings = (comparison: Comparison): Comparison => ({
  ...comparison,
  items: comparison.items.map(item => {
    if (item.type !== COMPARISON_ITEM_TYPES.GROUP) {
      return item;
    }

    const totalSize = item.groupItems?.reduce((sum, item) => sum + item.dollars, 0) || 0;

    const holdings = item.groupItems?.reduce((acc: ComparisonHolding[], groupItem) => {
      const sourceItem = comparison.items.find(i => i._id === groupItem.itemId);
      if (!sourceItem) {
        return acc;
      }

      return acc.concat(
        ...sourceItem.holdings.map(h => ({
          ...h,
          weight: h.weight * (groupItem.dollars / totalSize),
        })),
      );
    }, []);

    return {
      ...item,
      holdings: holdings || [],
    };
  }),
});

/**
 * Retrieves the applicable comparison items from a comparison based on the given filter.
 * @param comparison Comparison to get the filtered items from
 * @param filter Filter option
 */
export const getComparisonFilteredItems = (comparison: { items: ComparisonItem[] }, filter: string[]): ComparisonItem[] => {
  if (filter.some(f => f === ComparisonItemsFilter.All)) {
    return comparison.items;
  }

  const predicates: Array<(item: ComparisonItem) => boolean> = [
    // Filter based on the ids
    item => filter.some(f => item._id === f),
  ];

  if (filter.some(f => f === ComparisonItemsFilter.Groups)) {
    // Include all group items
    predicates.push(item => item.type === COMPARISON_ITEM_TYPES.GROUP);
  }

  if (filter.some(f => f === ComparisonItemsFilter.Products)) {
    // Include all non-group items
    predicates.push(item => item.type !== COMPARISON_ITEM_TYPES.GROUP);
  }

  // Keep any item for which at least one predicate returns true
  return comparison.items.filter(item => predicates.some(predicate => predicate(item)));
};

export const comparisonHasMissingItems = (comparison: Comparison) => !!comparison.missingItems && comparison.missingItems.length > 0;

export const comparisonHasUnallocatedPortfolioItem = (comparison: Comparison) =>
  !!comparison.modifiedItems?.some(comparisonItem => isUnallocatedPortfolioModification(comparisonItem));

export const comparisonHasPlanRemovedPortfolioItem = (comparison: Comparison) =>
  !!comparison.modifiedItems?.some(comparisonItem => isPlanRemovedPortfolioModification(comparisonItem));

export const comparisonHasModifiedItems = (comparison: Comparison) => !!comparison.modifiedItems && comparison.modifiedItems.length > 0;

export const comparisonHasMissingOrModifiedItems = (comparison: Comparison) => comparisonHasMissingItems(comparison) || comparisonHasModifiedItems(comparison);

export const comparisonIsMissingAllItems = (comparison: Comparison) => {
  if (!comparisonHasMissingItems(comparison)) {
    return false;
  }

  return comparison.items
    .filter(item => ![COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.BENCHMARK].includes(item.type as COMPARISON_ITEM_TYPES))
    .every(item => comparison.missingItems?.some(missingItem => missingItem._id === item._id));
};

export const comparisonHasAllUnallocatedPortfolios = (comparison: Comparison) =>
  comparison.items
    .filter(item => ![COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.BENCHMARK].includes(item.type as COMPARISON_ITEM_TYPES))
    .every(item => comparison.modifiedItems?.some(modifiedItem => modifiedItem.item._id === item._id && isUnallocatedPortfolioModification(modifiedItem)));

export const comparisonHasAllUnallocatedOrMissingItems = (comparison: Comparison) =>
  comparison.items
    .filter(item => ![COMPARISON_ITEM_TYPES.GROUP, COMPARISON_ITEM_TYPES.BENCHMARK].includes(item.type as COMPARISON_ITEM_TYPES))
    .every(
      item =>
        comparison.modifiedItems?.some(modifiedItem => modifiedItem.item._id === item._id && isUnallocatedPortfolioModification(modifiedItem)) ||
        comparison.missingItems?.some(missingItem => missingItem._id === item._id),
    );

/**
 * Retrieves the list of symbols from a comparison holdings list.
 * @param {ComparisonHolding[]} holdings items to iterate and get their symbols.
 * @return {string[]} a list of symbols.
 */
export const getHoldingsSymbols = (holdings: ComparisonHolding[]): string[] => holdings.map(({ symbol }) => symbol) as string[];

/**
 * Determine if the comparison settings needs check/uncheck monthly data
 * @param {string} option selected in comparison settings preferences
 * @return {boolean} to choose montly option
 */
export const setHypotheticalReturnChartDetail = (option: string): boolean => {
  switch (option) {
    case USER_PREFERENCES_HYPOTHETICAL_RETURN_CHART_DETAIL.MONTHLY:
      return true;
    default:
      return false;
  }
};

export const shouldUseMonthlyRollingInReturns = (comparison: Comparison, user?: Nullable<User>) =>
  shouldUseMonthlyRollingInReturnsForRebalancing(comparison.monthEndDataOption, user);

export const shouldUseMonthlyRollingInReturnsForRebalancing = (monthEndDataOption?: PERIOD_END_DATA_OPTIONS, user?: Nullable<User>) => {
  const canUseMonthlyRebalance = userHasPermission(user, PERMISSIONS.USE_MONTHLY_REBALANCE);
  const hasValidMonthEndOption = monthEndDataOption != null && monthEndDataOption !== PERIOD_END_DATA_OPTIONS.NONE;
  return canUseMonthlyRebalance && hasValidMonthEndOption;
};

export const shouldExpandPortfolioInReadOnly = (item: ComparisonItem, user?: Nullable<User>) => {
  const { holdings, type } = item;
  const isPortfolio = type === COMPARISON_ITEM_TYPES.PORTFOLIO;
  const hasReachedLimit = !!(holdings.length > MAX_EXPANDED_ITEMS_IN_PORTFOLIO);
  const hasPermission = userHasPermissionEnabled(user, PERMISSIONS.COMPARISON_PORTFOLIO_EXPAND_IN_READ_ONLY);
  return hasPermission && hasReachedLimit && isPortfolio;
};

/**
 * Filters out modified items from a comparison by modification key(s).
 * @param comparison The comparison.
 * @param keysToRemove An array of keys to remove.
 * @returns Filtered array of modified items.
 */
export const filterModifiedItemsByKeys = (comparison: Comparison, keysToRemove: COMPARISON_MODIFIED_ITEM_KEYS[]) =>
  comparison.modifiedItems?.filter(m => !keysToRemove.includes(m.modification));

/**
 * Checks if a modified item is an unallocated portfolio.
 * @param modifiedItem The modified item object.
 * @returns True if the item is a portfolio with UNALLOCATED_PORTFOLIO modification.
 */
export const isUnallocatedPortfolioModification = (modifiedItem: { item: ComparisonItem; modification: COMPARISON_MODIFIED_ITEM_KEYS }): boolean =>
  modifiedItem.item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && modifiedItem.modification === COMPARISON_MODIFIED_ITEM_KEYS.UNALLOCATED_PORTFOLIO;

/**
 * Checks if a plan was removed from the comparison item.
 * @param comparison The comparison object.
 * @param itemId The ID of the item to check.
 * @param data The data containing planning information.
 * @returns True if the plan was removed, false otherwise.
 */
export const isPlanRemovedPortfolioModification = (modifiedItem: { item: ComparisonItem; modification: COMPARISON_MODIFIED_ITEM_KEYS }): boolean =>
  modifiedItem.item.type === COMPARISON_ITEM_TYPES.PORTFOLIO && modifiedItem.modification === COMPARISON_MODIFIED_ITEM_KEYS.PLANNING_REMOVED;

/**
 * Apply universe exclusion filtering to an array of comparison items.
 * Filters out holdings and detailed holdings funds based on excluded universes,
 * and recalculates weights proportionally for remaining funds.
 *
 * @param items Array of comparison items to filter
 * @param excludedUniverses Array of universe names to exclude
 * @param locale Locale to use for universe text comparison (default: 'en-CA')
 * @param formatLocalizedStringFn Optional function to format localized strings (for custom formatting)
 */
export const applyUniverseExclusionToItems = (
  items: ComparisonItem[],
  excludedUniverses: string[],
  locale: string = 'en-CA',
  formatLocalizedStringFn?: (localizedString: LocalizedString, locale: string) => string,
): ComparisonItem[] => {
  if (!excludedUniverses || excludedUniverses.length === 0) {
    return items;
  }

  return items.map(item => {
    // Helper function to filter holdings based on universe exclusion
    const filterHoldingsByUniverse = <T extends { universe?: LocalizedString; weight: number }>(holdings: T[]): T[] =>
      holdings.filter(holding => {
        if (!holding.universe) {
          return true; // Include holdings without universe property
        }

        // Get the localized universe string
        const universeText = formatLocalizedStringFn
          ? formatLocalizedStringFn(holding.universe, locale)
          : holding.universe[locale as keyof typeof holding.universe] || holding.universe['en-CA'] || '';

        // Include holding if its universe is NOT in the exclusion list
        return !excludedUniverses.includes(universeText);
      });

    // Helper function to filter detailed holdings funds
    const processDetailedHoldings = (detailedHoldings: Holdings | undefined, remainingHoldingIds: Set<string>): Holdings | undefined => {
      if (!detailedHoldings?.funds) {
        return detailedHoldings;
      }

      // Filter funds to keep only those that correspond to remaining holdings
      const filteredFunds = detailedHoldings.funds.filter((fund: FundAllocation) => remainingHoldingIds.has(fund.original));

      return {
        ...detailedHoldings,
        funds: filteredFunds,
      };
    };

    // Filter main item holdings
    const filteredHoldings = filterHoldingsByUniverse(item.holdings);

    const remainingHoldingIds = new Set<string>();
    filteredHoldings.forEach(holding => remainingHoldingIds.add(holding.id));

    // Process main item detailed holdings
    const updatedDetailedHoldings = processDetailedHoldings(item.detailedHoldings, remainingHoldingIds);

    // Process source holdings and detailed holdings if they exist
    let updatedSource = item.source;
    if (item.source?.holdings) {
      const filteredSourceHoldings = filterHoldingsByUniverse(item.source.holdings);

      // For source detailed holdings, use the same remaining holding IDs as the main item
      // since the source detailed holdings should correspond to the main holdings
      const updatedSourceDetailedHoldings = processDetailedHoldings(item.source.detailedHoldings, remainingHoldingIds);

      updatedSource = {
        ...item.source,
        holdings: filteredSourceHoldings,
        detailedHoldings: updatedSourceDetailedHoldings,
      };
    }

    // Return item with filtered holdings and updated detailed holdings
    return {
      ...item,
      holdings: filteredHoldings,
      detailedHoldings: updatedDetailedHoldings,
      source: updatedSource,
    };
  });
};
